<script setup lang="ts">
import LeButton from "@dsk/components/Button/LeButton.vue";
import LeHeading from "@dsk/components/Heading/LeHeading.vue";
import LeFloatingContainer from "@dsk/components/LeFloatingContainer.vue";
import LeBell from "@dsk/icons/LeBell.vue";
import { storeToRefs } from "pinia";
import { computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useToast } from "vue-toastification";

import LeDictionarySelector from "@/components/dictionary-selector/LeDictionarySelector.vue";
import LeOmniDictionarySelector from "@/components/dictionary-selector/LeOmniDictionarySelector.vue";
import LeFrontPageCarousel from "@/components/LeFrontPageCarousel.vue";
import LeWordOfTheWeek from "@/components/LeWordOfTheWeek.vue";
import LeFreeSearchesLeftAlert from "@/components/searchbar/LeFreeSearchesLeftAlert.vue";
import LeSearchBar from "@/components/searchbar/LeSearchBar.vue";
import LeSearchChangedLanguageSelectorToast from "@/components/toasts/LeSearchChangedLanguageSelectorToast.vue";
import { useDictionarySelector, usePageFade, useResponsiveness } from "@/composables";
import { vMatomoEvent } from "@/directives";
import { SearchFilterHelper } from "@/helpers";
import LeLayoutMain from "@/layouts/LeLayoutMain.vue";
import LeSearchProvider from "@/providers/LeSearchProvider.vue";
import {
  useDictionariesStore,
  useFavoriteDictionariesStore,
  useSearchFilterStore,
  useSearchStore,
  useSuggestionsStore,
  useUserStore
} from "@/stores";
import { MatomoEventAction, MatomoEventCategory } from "@/types";

const i18n = useI18n();
const { t } = i18n;
const { fadeOutClass } = usePageFade({ fadeOut: true });
const { groupsWithDictionaries, onSelectDictionary } = useDictionarySelector();
const { isSmallerThanDesktopScreen } = useResponsiveness();
const toast = useToast();

const { userData } = storeToRefs(useUserStore());
const favoriteDictionariesStore = useFavoriteDictionariesStore();
const dictionariesStore = useDictionariesStore();
const searchStore = useSearchStore();
const searchFilterStore = useSearchFilterStore();
const suggestionsStore = useSuggestionsStore();

const isPaying = computed<boolean>(() => userData.value.paying);

watch(
  () => searchStore.selectedDictionaryId,
  (newId, oldId) => {
    if (newId === oldId) {
      return;
    }

    suggestionsStore.getSuggestions(
      searchStore.query,
      searchStore.selectedMethodIds ?? searchStore.prioritizedMethodIds
    );
  }
);

watch(
  () => searchFilterStore.filter,
  (filter) => {
    if (!searchStore.selectedDictionary) {
      return;
    }

    if (SearchFilterHelper.dictionaryIsCompatibleWithSearchFilter(searchStore.selectedDictionary, filter)) {
      return;
    }

    searchStore.selectedDictionaryId = undefined;

    toast.clear();

    toast(
      { component: LeSearchChangedLanguageSelectorToast, props: { i18n, type: "changed-dictionary" } },
      { timeout: 20000, icon: LeBell }
    );
  },
  { deep: true }
);
</script>

<template>
  <LeLayoutMain :simple-footer="isPaying">
    <div class="le-home-view">
      <LeOmniDictionarySelector :dictionaries="dictionariesStore.dictionaries" @select="onSelectDictionary($event)" />

      <div class="container le-home-view__search-container">
        <div
          v-if="!isSmallerThanDesktopScreen"
          v-shared-element:dictionary-selector
          class="le-home-view__dictionary-selector-container"
        >
          <LeDictionarySelector
            class="le-home-view__dictionary-selector"
            :has-loaded="dictionariesStore.status === 'loaded'"
            :company-dictionaries="dictionariesStore.companyDictionaries"
            :company-dictionary-group-heading="dictionariesStore.companyDictionaryGroupHeading"
            :favorite-dictionaries="favoriteDictionariesStore.dictionaries"
            :groups-with-dictionaries="groupsWithDictionaries"
            :selected-dictionary="searchStore.selectedDictionary"
            :popover-position="{ top: '-125px', left: '102%' }"
            count="dictionaries"
            show-all-dictionaries-option
            @select="onSelectDictionary($event)"
            @favorite="favoriteDictionariesStore.addDictionary($event)"
            @unfavorite="favoriteDictionariesStore.removeDictionary($event)"
          >
            <template v-if="searchStore.selectedDictionary" #buttonLabel>
              {{ searchStore.selectedDictionary.title }}
            </template>
          </LeDictionarySelector>
        </div>

        <LeSearchProvider
          v-slot="{
            query,
            suggestions,
            freeSearchesLeft,
            selectedDictionaryId,
            selectedMethodIds,
            prioritizedMethodIds,
            searchWithSearchFilter,
            getSuggestions,
            logSelectedSuggestion
          }"
        >
          <LeSearchBar
            v-shared-element:search-bar
            class="le-home-view__search-bar"
            autofocus
            focus-on-type
            :value="query"
            :free-searches-left="freeSearchesLeft"
            :suggestions="suggestions"
            @search="
              searchWithSearchFilter(
                $event.query,
                $event.filter,
                selectedDictionaryId,
                selectedDictionaryId ? false : true
              )
            "
            @change="getSuggestions($event, selectedMethodIds ?? prioritizedMethodIds)"
            @log-selection="logSelectedSuggestion($event)"
          />

          <LeFreeSearchesLeftAlert
            v-if="!isPaying"
            class="le-home-view__searches-left-alert"
            :free-searches-left="freeSearchesLeft"
          />
        </LeSearchProvider>
      </div>

      <div :class="fadeOutClass">
        <LeFrontPageCarousel v-if="isPaying" />
        <div v-else class="container">
          <div class="row le-home-view__flex-row">
            <div class="col-xs-12 col-md-8 col-md-push-4 le-home-view__flex-col">
              <LeFloatingContainer color="secondary" class="le-home-view__get-access">
                <LeHeading tag="h2" class="le-home-view__headline">
                  {{ t("le-free-page-view.get-access.header") }}
                </LeHeading>
                <p class="le-home-view__text">
                  {{ t("le-free-page-view.get-access.text-1") }}
                  <br />
                  <br />
                  {{ t("le-free-page-view.get-access.text-2") }}
                </p>
                <LeButton
                  v-matomo-event:click="{
                    category: MatomoEventCategory.FreePage,
                    action: MatomoEventAction.ClickGetAccessBox,
                    label: `Clicked 'Get access'`
                  }"
                  tag="router-link"
                  :to="{ name: 'subscription' }"
                  >{{ t("le-free-page-view.get-access.button") }}</LeButton
                >
              </LeFloatingContainer>
            </div>

            <div class="col-xs-12 col-md-4 col-md-pull-8 le-home-view__flex-col">
              <LeFloatingContainer color="secondary" class="le-home-view__image-container">
                <LeWordOfTheWeek />
              </LeFloatingContainer>
            </div>
          </div>
        </div>
      </div>
    </div>
  </LeLayoutMain>
</template>

<style lang="less" scoped>
@import "@dsk/styles/all";

.le-home-view {
  height: 100%;

  &__search-container {
    margin-top: 64px;
    margin-bottom: 64px;

    #media-breakpoint-up(sm, {
      margin-top: 144px;
      margin-bottom: 144px;
    });

    #media-breakpoint-up(lg, {
      display: flex;
      gap: 30px;
    });
  }

  &__dictionary-selector-container {
    position: relative;
  }

  &__dictionary-selector {
    flex-shrink: 0;
    width: 262px;
  }

  &__searches-left-alert {
    margin-top: 24px;
  }

  &__flex-row {
    display: flex;
    flex-wrap: wrap;
    margin-bottom: 50px;

    #media-breakpoint-up(lg, {
      margin-bottom: 64px;
    });
  }

  &__flex-col {
    display: flex;
    flex-direction: column;
  }

  &__get-access {
    height: 100%;
    margin-bottom: 30px;
    padding: 24px 16px;
    text-align: center;

    #media-breakpoint-up(lg, {
      padding: 25px;
      margin-bottom: 0;
      text-align: left;
    });

    #media-breakpoint-up(xl, {
      padding: 60px 60px 50px;
      margin-bottom: 0;
      text-align: left;
    });
  }

  &__headline,
  &__text {
    color: @colors[@neutrals][400];
  }

  &__text {
    margin-top: 12px;
    margin-bottom: 24px;
    .body-text("m");

    #media-breakpoint-up(lg, {
      margin-top: 16px;
      margin-bottom: 30px;
    });
  }

  &__image-container {
    height: 100%;
    padding: 4px;
  }

  // TODO This can probably be removed once we upgrade to Bootstrap 4+
  .container::before,
  .container::after {
    display: none;
    clear: none;
  }
}
</style>

<style lang="less">
// Due to a bug in HeadlessUI, we can't use scoped styles for this class
.le-home-view__search-bar {
  flex-grow: 1;
}
</style>
